import type { ColorMask, MassKind } from './types';

/** The plate (board) in world units. */
export const WORLD_W = 1200;
export const WORLD_H = 800;

export interface MassSpec {
  /** Lensing strength mu = 2GM in world units: weak-field deflection is 2 mu / b. */
  mu: number;
  /** Body radius (opaque). For a black hole this is the capture radius, equal to mu. */
  radius: number;
  /** Cost used for par and stars. */
  weight: number;
  name: string;
  plural: string;
}

/**
 * Tuned for play: a moon can tip a beam by a few degrees, a giant can turn it by tens of
 * degrees, a black hole captures anything inside its photon sphere (r = mu, see physics.ts).
 */
export const MASS_SPECS: Record<MassKind, MassSpec> = {
  moon: { mu: 4.5, radius: 14, weight: 1, name: 'Moon', plural: 'Moons' },
  star: { mu: 10, radius: 19, weight: 2, name: 'Star', plural: 'Stars' },
  giant: { mu: 20, radius: 26, weight: 3, name: 'Giant', plural: 'Giants' },
  hole: { mu: 26, radius: 26, weight: 4, name: 'Black hole', plural: 'Black holes' },
  void: { mu: -11, radius: 9, weight: 2, name: 'Void', plural: 'Voids' },
};

export const MASS_KINDS: readonly MassKind[] = ['moon', 'star', 'giant', 'hole', 'void'];

/** Radius of a beacon (it blocks beams like any solid object). */
export const SOURCE_RADIUS = 13;
/** Visual radius of a telescope. */
export const RECEIVER_RADIUS = 20;
/** A beam passing this close to a telescope's centre is caught. */
export const RECEIVER_CATCH = 26;
/** Half thickness of a wall. */
export const WALL_HALF = 6;
/** Minimum clearance between a placed mass and any solid element. */
export const PLACE_GAP = 4;

export const RED = 1;
export const GREEN = 2;
export const BLUE = 4;
export const WHITE = 7;

export const COLOR_NAMES: Record<number, string> = {
  1: 'red',
  2: 'green',
  3: 'yellow',
  4: 'blue',
  5: 'magenta',
  6: 'cyan',
  7: 'white',
};

export function colorName(c: ColorMask): string {
  return COLOR_NAMES[c] ?? 'dark';
}

export function isValidColor(c: unknown): c is ColorMask {
  return typeof c === 'number' && Number.isInteger(c) && c >= 1 && c <= 7;
}
